import { Box, Button, Stack, styled } from "@mui/material";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { API } from "aws-amplify";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";
import ClipboardCopyButton from "./ClipboardCopyButton";
import { ModalSpinner } from "./ModalSpinner";
import * as SC from "./RecipientDetails.style";
import ResizableDiv from "./ResizableDiv";

export const Container = styled(Stack)`
  flex: 1 1 auto;
  flex-direction: column;
  overflow-y: auto;
  gap: 1rem;
  padding: 1rem 2rem 0 2rem;

  @media screen and (max-width: 1024px) {
    padding: 1rem 1rem 0 1rem;
  }
`;

const Paper = styled(SC.Paper)`
  flex-direction: column;
  gap: 0.5rem;
`;

const Title = styled(SC.SubTitle)`
  font-size: 1rem;
`;

const Description = styled(Box)`
  font-size: 12px;
  font-weight: 500;
  line-height: 16.8px;
  text-align: left;
  color: #71839b;
`;

const LinkPreview = styled(Box)`
  width: 100%;
  padding: 15px;

  font-size: 12px;
  font-weight: 500;
  line-height: 16.8px;
  text-align: left;

  color: #272727;
  border-radius: 4px;
  background: #f3f6f8;
  border: 1px solid #e3eaf3;
`;

export const ReferralFormCreation = () => {
  const dispatch = useAppDispatch();
  const { createApiCall } = useCreateApiCall();
  const { userData } = useContext(GlobalContext);
  const id = userData?.organizationId;
  const [data, setData] = useState(null);

  const [isLoading, setIsLoading] = useState(true);

  const { externalFormLink } =
    // @ts-ignore
    data?.Item ?? {};

  const getData = createApiCall(
    async () => {
      setIsLoading(true);
      // load recipient details by recipient id
      dispatch(showSpinner());
      const result = await API.get("restApi", `/recipients/${id}`, {});
      setData(result);
      dispatch(hideSpinner());
      setIsLoading(false);
    },
    () => setIsLoading(false)
  );

  const handleCreateEmbeddedCode = createApiCall(async () => {
    // load recipient details by recipient id
    dispatch(showSpinner());
    const result = await API.post("restApi", `/recipients`, {
      body: {
        action: "create_embedded_code",
      },
    });
    dispatch(hideSpinner());
    setIsLoading(false);
    if (result?.code) {
      getData();
    }
  });

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const embeddedFormUrl =
    process.env.REACT_APP_CURRENT_ENV === "dev"
      ? `https://button-dev.referral-stream.com/embedded_form/${externalFormLink}`
      : `https://button.referral-stream.com/embedded_form/${externalFormLink}`;

  const shortFormUrl =
    process.env.REACT_APP_CURRENT_ENV === "dev"
      ? `https://refstr-dev.com/${externalFormLink}`
      : `https://refstr.com/${externalFormLink}`;

  const htmlCode = `<div style="position: relative;width: 100%;padding-bottom: 310px;height: 0;overflow: hidden;">
  <iframe
      src="${embeddedFormUrl}"
      frameborder="0"
      allowfullscreen
      style="position: absolute;top: 0;left: 0;width: 100%;height: 300px;overflow: hidden;">
  </iframe>
</div>`;

  return (
    <CommonOutletTemplate noPadding title="Referral Form">
      {!!data && (
        <Container>
          <Paper>
            {!externalFormLink && (
              <>
                <Title>Embedded Referral Form and Direct Link</Title>
                <Description>
                  Generate an embeddable HTML code and a direct link for your
                  Referral Stream form. The HTML code can be integrated into
                  your website to create a seamless patient experience, while
                  the direct link can be shared via email, messaging, or social
                  media to give patients easy access to submit referrals and
                  questions
                </Description>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateEmbeddedCode}
                  sx={{ width: "fit-content", marginTop: "20px" }}
                >
                  Create Form and Link
                </Button>
              </>
            )}
            {!!externalFormLink && (
              <>
                <Title>Direct Link</Title>
                <Description>
                  Use the direct link to your organization's referral form
                  anywhere you’d like - such as in an email to your Senders,
                  your email signature, or in other marketing materials. This
                  link provides quick, straightforward access to the referral
                  form.
                </Description>
                <LinkPreview>{shortFormUrl}</LinkPreview>
                <ClipboardCopyButton
                  textToCopy={shortFormUrl}
                  label={"Copy"}
                  copiedText={"Direct Link Copied"}
                />
                <Title sx={{ marginTop: "30px" }}>Embed HTML Code</Title>
                <Description>
                  Use the HTML code to display your organization's referral form
                  directly on your website. This offers a seamless experience
                  for your Senders, allowing them to complete and submit
                  referrals without leaving your site. Simply copy the embed
                  code and share it with your webmaster (or whoever manages your
                  site) to get started.
                </Description>
                <ResizableDiv>
                  <div dangerouslySetInnerHTML={{ __html: htmlCode }} />
                </ResizableDiv>
                <ClipboardCopyButton
                  textToCopy={htmlCode}
                  label={"Copy"}
                  copiedText={"Embedded HTML Code Copied"}
                />
              </>
            )}
          </Paper>
        </Container>
      )}
      {isLoading && <ModalSpinner open={true} />}
    </CommonOutletTemplate>
  );
};
